/**
 * Merged Metal Color + Purity swatch picker.
 * Uses event delegation so clicks keep working after variant-selects DOM is replaced.
 * On load, if the current selection is out of stock, selects the next in-stock
 * purity for the same metal (e.g. 22k OOS → 18k).
 *
 * Important: both metal + purity radios must be updated before firing change,
 * otherwise Shopify receives a mixed combo (e.g. Platinum + 9k) and falls back
 * to the first available 9k variant.
 */
(function () {
  if (window.__mergedVariantPickerInit) return;
  window.__mergedVariantPickerInit = true;

  function findFieldset(vs, optionName) {
    return Array.from(vs.querySelectorAll('.merged-hidden-fieldset')).find(
      (fs) => fs.dataset.optionName === optionName
    );
  }

  function findRadio(fieldset, { value, optionValueId }) {
    if (!fieldset) return null;
    const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    if (optionValueId) {
      const byId = radios.find((radio) => String(radio.dataset.optionValueId) === String(optionValueId));
      if (byId) return byId;
    }
    if (value != null && value !== '') {
      return radios.find((radio) => radio.value === value) || null;
    }
    return null;
  }

  function syncHiddenRadios(vs, metalOptionName, purityOptionName, metal, purity, metalOptionValueId, purityOptionValueId) {
    let metalRadio = null;
    let purityRadio = null;

    const metalFieldset = findFieldset(vs, metalOptionName);
    const purityFieldset = findFieldset(vs, purityOptionName);

    if (metalFieldset) {
      metalFieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.checked = false;
      });
      metalRadio = findRadio(metalFieldset, { value: metal, optionValueId: metalOptionValueId });
      if (metalRadio) metalRadio.checked = true;
    }

    if (purityFieldset) {
      purityFieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.checked = false;
      });
      purityRadio = findRadio(purityFieldset, { value: purity, optionValueId: purityOptionValueId });
      if (purityRadio) purityRadio.checked = true;
    }

    return { metalRadio, purityRadio };
  }

  function collectSelectedOptionValueIds(vs, metalRadio, purityRadio, metalOptionValueId, purityOptionValueId) {
    const ids = [];
    const metalFieldset = metalRadio?.closest('fieldset');
    const purityFieldset = purityRadio?.closest('fieldset');

    vs.querySelectorAll('select option[selected], fieldset input:checked').forEach((el) => {
      const fieldset = el.closest?.('fieldset');
      // Skip stale metal/purity checks; we append the intended IDs below.
      if (fieldset && (fieldset === metalFieldset || fieldset === purityFieldset)) return;
      if (el.dataset?.optionValueId) ids.push(el.dataset.optionValueId);
    });

    const metalId = metalRadio?.dataset.optionValueId || metalOptionValueId;
    const purityId = purityRadio?.dataset.optionValueId || purityOptionValueId;
    if (metalId) ids.push(String(metalId));
    if (purityId) ids.push(String(purityId));

    return ids.filter(Boolean);
  }

  function triggerVariantUpdate(vs, targetRadio, selectedOptionValues) {
    if (targetRadio) {
      targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // Radios missing (should be rare with merged_mode) — still request the correct combo.
    if (!selectedOptionValues?.length || typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    const productInfo = vs.closest('product-info');
    const productUrl = productInfo?.dataset?.url || '';
    const fakeTarget = {
      id: `merged-swatch-${vs.dataset.section || 'product'}`,
      dataset: { productUrl },
      tagName: 'INPUT',
      type: 'radio',
      value: '',
    };

    publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: {
        event: { target: vs },
        target: fakeTarget,
        selectedOptionValues,
      },
    });
  }

  function updateMergedUI(picker, swatch) {
    const selectedLabel = picker.querySelector('[data-merged-selected-label]');
    picker.querySelectorAll('.merged-variant__swatch').forEach((el) => {
      el.classList.remove('is-selected');
    });
    swatch.classList.add('is-selected');
    if (selectedLabel) {
      selectedLabel.textContent = swatch.dataset.label || `${swatch.dataset.purity} ${swatch.dataset.metal}`;
    }
  }

  function selectSwatch(vs, picker, swatch, fireChange) {
    if (!swatch || swatch.classList.contains('is-disabled')) return false;

    const metal = swatch.dataset.metal;
    const purity = swatch.dataset.purity;
    const metalOptionValueId = swatch.dataset.metalOptionValueId || '';
    const purityOptionValueId = swatch.dataset.purityOptionValueId || '';
    const metalOptionName = vs.dataset.mergedMetalOption;
    const purityOptionName = vs.dataset.mergedPurityOption;
    if (!metal || !purity || !metalOptionName || !purityOptionName) return false;

    const { metalRadio, purityRadio } = syncHiddenRadios(
      vs,
      metalOptionName,
      purityOptionName,
      metal,
      purity,
      metalOptionValueId,
      purityOptionValueId
    );

    // Both options must be applied together. If purity radio is missing, do not
    // fire a metal-only change (that caused Platinum → 9k fallback).
    const hasPair = Boolean(
      (metalRadio && purityRadio) || (metalOptionValueId && purityOptionValueId)
    );
    if (!hasPair) return false;

    updateMergedUI(picker, swatch);

    const sectionId = vs.dataset.section;
    const variantId = swatch.dataset.variantId;
    if (sectionId && variantId && window.VariantMediaPreload) {
      window.VariantMediaPreload.switchInstant(sectionId, variantId);
    }

    if (fireChange) {
      const selectedOptionValues = collectSelectedOptionValueIds(
        vs,
        metalRadio,
        purityRadio,
        metalOptionValueId,
        purityOptionValueId
      );
      triggerVariantUpdate(vs, purityRadio || metalRadio, selectedOptionValues);
    }
    return true;
  }

  function ensureAvailableSelection(vs) {
    const picker = vs.querySelector('.merged-variant-picker');
    if (!picker) return;

    const swatches = Array.from(
      picker.querySelectorAll('.merged-variant__swatch:not(.is-disabled)')
    );
    if (!swatches.length) return;

    const availableVariantId = vs.dataset.mergedAvailableVariantId || '';
    const productForm = document.getElementById(`product-form-${vs.dataset.section}`);
    const variantInput = productForm && productForm.querySelector('input[name="id"]');
    const formNeedsSync =
      availableVariantId && variantInput && String(variantInput.value) !== String(availableVariantId);

    const selected = picker.querySelector('.merged-variant__swatch.is-selected:not(.is-disabled)');
    if (selected && !formNeedsSync) {
      selectSwatch(vs, picker, selected, false);
      return;
    }

    let next = null;
    if (availableVariantId) {
      next = swatches.find((s) => String(s.dataset.variantId) === String(availableVariantId));
    }

    // Prefer the already-selected swatch's metal when falling back (never jump to 9k of another metal).
    const preferredMetal =
      (selected && selected.dataset.metal) || vs.dataset.mergedPreferredMetal || '';
    if (!next && preferredMetal) {
      next = swatches.find((s) => s.dataset.metal === preferredMetal);
    }
    if (!next) next = selected || swatches[0];

    selectSwatch(vs, picker, next, true);

    const variantId = next?.dataset.variantId;
    if (variantId && window.VariantMediaPreload) {
      window.VariantMediaPreload.switchInstant(vs.dataset.section, variantId);
    }
  }

  function initAll() {
    document.querySelectorAll('variant-selects').forEach((vs) => {
      if (!vs.querySelector('.merged-variant-picker')) return;
      ensureAvailableSelection(vs);
    });
  }

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('.merged-variant__swatch');
    if (!swatch || swatch.classList.contains('is-disabled')) return;

    const vs = swatch.closest('variant-selects');
    const picker = swatch.closest('.merged-variant-picker');
    if (!vs || !picker) return;

    event.preventDefault();
    selectSwatch(vs, picker, swatch, true);
  });

  document.addEventListener(
    'mouseenter',
    (event) => {
      const swatch = event.target.closest?.('.merged-variant__swatch');
      if (!swatch || swatch.classList.contains('is-disabled')) return;

      const vs = swatch.closest('variant-selects');
      const sectionId = vs?.dataset.section;
      const variantId = swatch.dataset.variantId;
      if (!sectionId || !variantId || !window.VariantMediaPreload) return;

      window.VariantMediaPreload.preloadVariant(sectionId, variantId);
      window.VariantMediaPreload.prefetchSection(sectionId, variantId);
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // After Shopify replaces variant-selects HTML, re-ensure selection.
  document.addEventListener('shopify:section:load', initAll);
})();
